/**
 * 模版市场-列表组件
 * @author VenDream, chenqunfeng
 * @since 18/06/08
 */
import isEqual from '@fe/common/lib/equal';
import { checkInViewport } from '@fe/common/lib/node';
import * as qs from '@fe/common/lib/query-string';
import Stat from '@fe/common/lib/stat';
import throttle from '@fe/common/lib/throttle';
import GotoTop from 'components/goto-top';
import NavCollection from 'components/nav-collection';
import NavEnterprice from 'components/nav-enterprice';
import PosterSettingDialog from 'components/poster-setting-dialog';
import toast from 'components/toast';
import FloatVideo from 'components/suspended-video';
import VARIABLES from 'constants/variable.json5';
import TplWaterfall from 'containers/tpl-waterfall';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { gotoEditor, sendStat } from 'utils/common';
import { isModuleHidden } from 'utils/hidden-modules';
import localCache from 'utils/searchCache';
import { getPayTemplate, isOrgUser } from 'utils/user';
import BottomStatus from './bottom-status';
import MyusedEmptyTips from './myused-empty-tips';
import OrgEmptyTips from './org-empty-tips';
import OrgUpdateTips from './org-update-tips';
import sendStoreViewStat from './stat-views';
import { checkUserOrgZoneStatus } from 'api/store';
import { isShowRabbitpre } from 'utils/env';
import './store-tpls.less';

// 空白盒子高度
const BLANK_BOX_HEIGHT = 430;

/**
 * 隐藏付费模板选项列表
 */
const TPL_HIDE_CATEGORY = [
  'RECOMMEND', //推荐模版
  'RP_H5', //翻页
  'RP_SPA', //长页
  'VIP', //会员专享
  'SEARCH', //搜索
  'POSTER', //海报
  'CHAJIAN', //插件
  'SHOWPAGE', // 合并翻页、长页为展示页
  'OPENZONE', // 平台专区
];
/**
 * 隐藏付费模板过滤列表
 */
const TPL_HIDE_FILTER = [
  'originalPrice', //价格
  'discountRatio', //折扣
  'depreciation', //立减
  'tplCornerLabel', //TOP10
];

class StoreTpls extends Component {
  static propTypes = {
    user: PropTypes.object,
    tplids: PropTypes.array,
    keywords: PropTypes.string,
    category: PropTypes.string,
    status: PropTypes.number,
    filters: PropTypes.object,
    isSearchType: PropTypes.bool,
    clearStoreTpls: PropTypes.func,
    getStoreTpls: PropTypes.func,
    getSpecialZoneTpls: PropTypes.func,
    orgZoneSearchFilters: PropTypes.object,
    getOrgZoneFilters: PropTypes.func,
    setTplLikeStatus: PropTypes.func,
    clearSubmitFilters: PropTypes.func,
    setActiveTab: PropTypes.func,
    togglePosterModal: PropTypes.func,
    showPosterModal: PropTypes.bool,
    openSearchFilters: PropTypes.object,
  };

  constructor() {
    super();

    // 分页配置
    this.pageNo = 1;
    this.pageSize = 11;

    // 是否禁用滚动加载
    this.banScroll = false;

    this.state = {
      // 是否开通了企业专区
      isOpenOrgZone: true,
    };
  }

  async componentDidMount() {
    const { user, category } = this.props;
    // 滚动监听
    window.addEventListener('scroll', throttle(this.handleScrollLoading, 100));

    if (category === VARIABLES.MY_TPL && user.guest) {
      // 未登录用户浏览已收藏模版Tab时提示登录
      // 确认用户当前为游客身份,即已经确认用户未登录
      const showSSODialog = window.COMMON_NAVBAR.showSSODialog;
      showSSODialog && showSSODialog('login', true);
    } else {
      // 获取初始数据
      this.fetchInitTplData();
    }

    // 企业专区tab需校验用户是否开通企业专区
    if (category === VARIABLES.ENTERPRISE) {
      const isOpenOrgZone = await checkUserOrgZoneStatus(user?.organizeId);
      this.setState({ isOpenOrgZone });
    }

    // 统计访问量
    sendStoreViewStat(this.props.category);
  }

  async componentDidUpdate(prevProps) {
    const thisProps = this.props;
    const {
      user,
      filters,
      category,
      keywords,
      isSearchType,
      openSearchFilters,
      orgZoneSearchFilters,
      getOrgZoneFilters,
    } = prevProps;
    const hasChangedKeywords = keywords !== thisProps.keywords;
    const hasChangedCategory = category !== thisProps.category;
    const hasChangedFilters =
      !isEqual(filters, thisProps.filters) ||
      !isEqual(openSearchFilters, thisProps.openSearchFilters) ||
      !isEqual(orgZoneSearchFilters, thisProps.orgZoneSearchFilters);
    const hasRefreshedUserInfo = !user.userId && thisProps.user.userId;
    const { organizeId: orgId } = user || {};

    // 未登录用户浏览[已收藏模版]Tab时提示登录
    // 确认用户当前为游客身份,即已经确认用户未登录
    if (thisProps.category === VARIABLES.MY_TPL && thisProps.user.guest) {
      const showSSODialog = window.COMMON_NAVBAR.showSSODialog;
      showSSODialog && showSSODialog('login', true);
      sendStoreViewStat(thisProps.category);
      return false;
    }

    // 在[已收藏模版]Tab刷新用户信息时，触发数据加载
    if (hasRefreshedUserInfo && thisProps.category === VARIABLES.MY_TPL) {
      this.fetchTplData();
      sendStoreViewStat(thisProps.category);
    }

    // 在企业专区Tab刷新用户信息时
    if (hasRefreshedUserInfo && thisProps.category === VARIABLES.ENTERPRISE) {
      // 重新获取企业专区筛选项
      getOrgZoneFilters(orgId);
      // 重新校验用户是否开通企业专区
      const isOpenOrgZone = await checkUserOrgZoneStatus(user?.organizeId);
      this.setState({ isOpenOrgZone });
      // 获取模板数据
      this.fetchTplData();
    }

    // 模板搜索页 - 数据条件更新推荐列表不触发加载
    if (isSearchType && category === VARIABLES.RECOMMEND) {
      return;
    }
    if (hasChangedKeywords || hasChangedCategory || hasChangedFilters) {
      // 切换分类时发生统计数据
      hasChangedCategory && sendStoreViewStat(thisProps.category);
      //刷新页面防止重新从后台拿数据
      if (thisProps.filters.isUrlParam && hasChangedFilters) {
        return;
      }
      // 变更关键词，切换分类或更新筛选条件时，重新获取模版数据
      thisProps.clearStoreTpls();
      this.pageNo = 1;
      const data = await this.fetchTplData('click');

      // 统计搜索结果数据
      if (hasChangedKeywords) {
        let hasResult = false;

        if (data && data.data && data.data.length) {
          hasResult = true;
        }
        sendStat('RP_STORE', '模版市场', '搜索结果', Stat.ACTION_CLICK, {
          id: 'web_mobanmarket_search_click',
          result: hasResult,
          keyword: thisProps.keywords,
        });
      }
    }
  }

  componentWillUnmount() {
    // 取消滚动监听
    window.removeEventListener(
      'scroll',
      throttle(this.handleScrollLoading, 100)
    );
  }

  // 渲染模版列表
  getTplsList() {
    const {
      category,
      status,
      tplids,
      user,
      keywords,
      isSearchType,
    } = this.props;
    const initItems = [];
    this.banScroll = false;
    // 未登录用户查看已收藏模版
    if (category === VARIABLES.MY_TPL && !user.userId) {
      this.banScroll = true;
      return <div className="empty-tips">请登录后查看</div>;
    }

    // 搜索页面 无数据
    if (
      status === 0 &&
      tplids &&
      !tplids.length &&
      category === VARIABLES.SEARCH &&
      isSearchType
    ) {
      this.banScroll = true;
      return null;
    }

    // 非具有筛选项的tab下只是简单返回提示
    if (
      status === 0 &&
      tplids &&
      !tplids.length &&
      (category === VARIABLES.MY_TPL ||
        category === VARIABLES.ENTERPRISE ||
        category === VARIABLES.VIP)
    ) {
      this.banScroll = true;
      return <div className="empty-tips">暂无数据</div>;
    }

    // 没有数据
    if (status === 0 && tplids && !tplids.length && !keywords) {
      this.banScroll = true;
      return (
        <div className="empty-tips">
          <p>
            暂无数据，换个筛选条件试试？
            <span
              onClick={() => {
                this.props.clearSubmitFilters();
                const subFilterLists = Array.prototype.slice.call(
                  document.querySelectorAll('.filters-list .sub-filters-list')
                );
                for (const li of subFilterLists) {
                  const active = li.querySelector('.active');
                  const all = li.querySelector('.sub-filter');
                  if (active) {
                    active &&
                      active.classList &&
                      active.classList.remove('active');
                    all && all.classList && all.classList.add('active');
                  }
                }
                sendStat(
                  'RP_STORE',
                  '模版市场',
                  '无结果清空筛选按钮',
                  Stat.ACTION_CLICK,
                  {
                    id: 'web_mobanmarket_moban_cleanfilter_click',
                  }
                );
              }}>
              清空筛选
            </span>
          </p>
          {category === VARIABLES.POSTER && (
            <div
              className="store-tpl-box create-new"
              title="创建空白模板"
              onClick={this.handleCreateNew}>
              <div className="create-icon" />
              <span>新建空白模板</span>
            </div>
          )}
        </div>
      );
    }
    // 加载中
    if (status === 1 && tplids && !tplids.length) {
      this.banScroll = true;
      return null;
    }

    // 翻页、长页合并为展示页了，所以下面只判断展示页
    // [模板市场][展示页][活动玩法][海报]分类下添加创建空白模版的盒子
    const categorys = [VARIABLES.SHOWPAGE, VARIABLES.POSTER];
    if (isOrgUser()) {
      // 只有非个人身份，玩法页面才有创建空白模板的盒子
      categorys.push(VARIABLES.CHAJIAN);
    }

    if (categorys.includes(category) && !keywords) {
      initItems.push({
        height: BLANK_BOX_HEIGHT,
        content: (
          <div
            className="store-tpl-box create-new"
            title="创建空白模板"
            onClick={this.handleCreateNew}>
            <div className="create-icon" />
            <span>空白模板</span>
          </div>
        ),
      });
    }

    return (
      tplids && (
        <TplWaterfall
          tplids={tplids}
          category={category}
          initItems={initItems}
        />
      )
    );
  }

  // 获取初始模版数据
  fetchInitTplData() {
    this.fetchTplData();
  }

  // 获取模版数据
  async fetchTplData(type) {
    const {
      keywords,
      category,
      filters,
      isSearchType,
      openSearchFilters = {},
      getSpecialZoneTpls,
      orgZoneSearchFilters,
      getStoreTpls,
    } = this.props;
    const { userId, organizeId, platformConfigInfo } = this.props.user;
    // 隐藏模版市场付费模版
    let hidePayTemplate = false;
    const ishidePayTemplate = getPayTemplate(platformConfigInfo);
    //  开放平台下 配置了 隐藏付费模板
    if (ishidePayTemplate) {
      // 筛选选项是否包含价格
      let haveFilterKey = Object.keys(filters).some(key =>
        TPL_HIDE_FILTER.includes(key)
      );
      if (TPL_HIDE_CATEGORY.includes(category) || haveFilterKey) {
        hidePayTemplate = true;
      }
    }

    // 搜索引擎缓存页Id
    const searchScrollId = this.getSearchScrollId();
    // const scrollId =
    //   category === VARIABLES.SEARCH && this.pageNo !== 1
    //     ? searchScrollId
    //     : null;
    const scrollId = this.pageNo !== 1 ? searchScrollId : null;
    let storeFilters;
    try {
      // 搜索页 从路由获取筛选项数据
      const queryObj = qs.parse(window.location.search) || {};

      storeFilters = queryObj.filters;
      storeFilters = JSON.parse(storeFilters) || [];
    } catch (error) {
      storeFilters = [];
    }

    //判断下filters是否为{};
    const isEmptyObj = JSON.stringify(filters) !== '{}';
    delete filters.isUrlParam;
    let filtersData = isSearchType
      ? category === VARIABLES.RECOMMEND
        ? {}
        : (isEmptyObj && filters) || storeFilters
      : filters;
    if (category === VARIABLES.OPENZONE || category === VARIABLES.ENTERPRISE) {
      let filters = {};
      if (category === VARIABLES.OPENZONE) {
        // 获取最新的mostHot值
        const { mostHot } = storeFilters;
        filters =
          mostHot !== undefined
            ? {
                ...openSearchFilters,
                mostHot,
              }
            : openSearchFilters;
      } else {
        filters = { ...orgZoneSearchFilters, userId, organizeId };
      }
      return await getSpecialZoneTpls({
        category,
        pageNo: this.pageNo++,
        pageSize: this.pageSize,
        filters,
        hidePayTemplate,
      });
    } else {
      return await getStoreTpls({
        category,
        keyword: keywords,
        filterItems: filtersData,
        pageNo: this.pageNo++,
        pageSize: this.pageSize,
        userId,
        organizeId: organizeId,
        hidePayTemplate,
        scrollId,
        loadType: type,
      });
    }
  }

  /** 获取搜索页 搜索引擎缓存页Id  */
  getSearchScrollId = () => {
    let scrollId = '';
    const cache = localCache.get();
    if (cache) {
      const { data } = cache;
      scrollId = data;
    }
    return scrollId;
  };

  /** 更新 搜索引擎缓存页Id */
  setSearchScrollId = () => {
    const cache = localCache.get();
    if (cache) {
      localCache.set({
        ...cache,
        expiredAt: new Date().getTime() + localCache.duration,
      });
    }
  };

  // 跳转模版市场首页
  gotoIndex = () => {
    if (window.parent !== window) {
      location.href = '/template/recommend.html';
      return;
    } else {
      window.location.href = '/';
    }
  };

  // 升级企业号
  handleOrgUpdate = () => {
    // 统计企业升级按钮点击量
    sendStat(
      'RP_STORE',
      '企业专享页点击企业服务页',
      '点击量',
      Stat.ACTION_CLICK,
      {
        id: 'web_mobanmarket_mobanqiye_qiye_click',
      }
    );

    const { user } = this.props;
    if (user && user.userId) {
      // 登录用户跳转到工作台企业申请
      location.href = `${window.RPCONFIG.workbenchHost}/company/info`;
    } else {
      // 未登录时弹出登录框
      const showSSODialog = window.COMMON_NAVBAR.showSSODialog;
      showSSODialog && showSSODialog('login', true);
    }
    return;
  };

  // 开通企业专区
  handleGoOrgService() {
    location.href = `${window.RPCONFIG.mainHost}/service/`;
  }

  // 创建新作品
  handleCreateNew = () => {
    const { category, user, togglePosterModal } = this.props;
    const blockedFlag = VARIABLES.BLOCKED_CODE.includes(+user.disabledState);
    if (blockedFlag)
      return toast.show({
        type: 'error',
        msg: VARIABLES.BLOCKED_TEXT.CREATE,
        delay: 2000,
      });
    if (category === VARIABLES.POSTER) {
      // 弹出设置海报尺寸窗口
      togglePosterModal(true);
    } else {
      gotoEditor('', category, category);
    }
  };

  // 滚动加载
  handleScrollLoading = () => {
    if (this.props.status !== 0 || !this.trigger) return false;
    const isTriggered = checkInViewport(this.trigger, undefined, 100);
    // const isTriggered = checkInViewport(this.trigger, document.body);
    // 小游戏暂不支持分页，滚动加载
    if (
      !this.banScroll &&
      isTriggered &&
      this.props.category !== VARIABLES.RP_GAME &&
      this.props.category !== VARIABLES.OPENEDPLUGIN
    ) {
      this.fetchTplData();
      if (this.props.category === VARIABLES.SEARCH) {
        this.setSearchScrollId();
      }
    }
  };

  render() {
    const {
      user,
      status,
      category,
      filters,
      tplids,
      showPosterModal,
      togglePosterModal,
    } = this.props;

    const { isOpenOrgZone } = this.state;
    const showRabbitpreLogo = isShowRabbitpre();

    // 是否企业用户
    const isOrgUser = user && user.organizeId;

    // 是否企业专区
    const isOrgTab = category === VARIABLES.ENTERPRISE;
    // 是否我使用过的模版
    const isMyUsedTab =
      category === VARIABLES.MY_TPL && filters && filters.myTplType === '2';

    // 企业专区是否为空
    const isOrgEmpty = isOrgTab && isOrgUser && status === 0 && !tplids.length;
    // 我使用过是否为空
    const isMyTplEmpty = isMyUsedTab && status === 0 && !tplids.length;
    // 屏幕高度
    const clientHeight = document.body.clientHeight;
    const storeTplStyle = {
      // 最小高度为iframe高度减去tab高度和marin高度
      minHeight: `${clientHeight - 24 - 50}px`,
    };

    // 是否隐藏右侧收藏
    const isNavCollectionHidden = isModuleHidden('nav-collection');

    // 是否隐藏 企业会员免费领取
    const isGetFreeVipHidden = isModuleHidden('get-free-enterprise-vip');

    // 是否显示开通企业专区提示
    const isOpenOrgTip = isOrgUser && !isOpenOrgZone;

    return (
      <div
        className={`cmp store-tpls ${category} transitionable`}
        style={storeTplStyle}>
        {(isOrgTab && !isOrgUser) || (isOrgTab && isOpenOrgTip) ? (
          <OrgUpdateTips
            handleOrgUpdate={this.handleOrgUpdate}
            handleGoOrgService={this.handleGoOrgService}
            isOpenOrgTip={isOpenOrgTip}
          />
        ) : isOrgEmpty ? (
          <OrgEmptyTips />
        ) : isMyTplEmpty ? (
          <MyusedEmptyTips gotoIndex={this.gotoIndex} />
        ) : (
          <div className="store-tpls-wrapper">
            {this.getTplsList()}
            <div className="loading-status" ref={node => (this.trigger = node)}>
              <BottomStatus status={status} />
            </div>
          </div>
        )}
        {!isNavCollectionHidden ? (
          <NavCollection setActiveTab={this.props.setActiveTab} />
        ) : null}

        {!isGetFreeVipHidden ? <NavEnterprice /> : null}
        <GotoTop />
        {showPosterModal ? (
          <PosterSettingDialog hide={togglePosterModal} />
        ) : null}
        {showRabbitpreLogo && <FloatVideo />}
      </div>
    );
  }
}

export default StoreTpls;
